import React, { useState, useEffect } from 'react';
import { X, CheckCircle, MessageSquare } from 'lucide-react';
import api from '../../../services/api';
import { formatVariance, formatDate } from './varianceConfig';

const AcknowledgeModal = ({ result, monitor, onClose, onSaved }) => {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [existingAcks, setExistingAcks] = useState([]);
  const [loadingAcks, setLoadingAcks] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.routeIntelligence.getAcknowledgments(result.result_id);
        setExistingAcks(res.data);
      } catch {
      } finally {
        setLoadingAcks(false);
      }
    })();
  }, [result.result_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await api.routeIntelligence.acknowledge({ resultId: result.result_id, note: note.trim() });
      onSaved();
    } catch (err) {
      setError(
        err.response?.status === 409
          ? 'Already acknowledged.'
          : err.response?.data?.error || 'Failed'
      );
    } finally {
      setSaving(false);
    }
  };

  const isBreach = Math.abs(result.variance_percent) > monitor.variance_threshold;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4">
      <div className="bg-surface rounded-lg border border-gray-200 w-full max-w-sm shadow-xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-primary-500" />
            <h2 className="text-sm font-semibold text-slate-800">Acknowledge Variance</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-px bg-gray-100 border-b border-gray-100">
          {[
            { label: 'Date', value: formatDate(result.result_date), color: 'var(--text-strong)' },
            {
              label: 'Variance',
              value: formatVariance(result.variance_percent),
              color: isBreach ? 'var(--danger)' : 'var(--accent)',
            },
            { label: 'Streak', value: `Day ${result.streak_day}`, color: 'var(--warning)' },
          ].map((x, i) => (
            <div key={i} className="bg-surface px-3 py-2 text-center">
              <div className="text-xs font-bold tabular-nums" style={{ color: x.color }}>
                {x.value}
              </div>
              <div className="text-[9px] text-gray-400 uppercase tracking-wider mt-0.5">
                {x.label}
              </div>
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="px-4 py-4">
          {error && (
            <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-md text-xs text-red-600">
              {error}
            </div>
          )}
          <label className="block text-[11px] font-medium text-gray-500 mb-1.5">
            Action taken / reason *
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Describe action taken..."
            className="w-full px-3 py-2 bg-surface border border-gray-200 rounded-md text-sm text-slate-800 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 focus:outline-none placeholder:text-gray-400 resize-none"
            rows={3}
            required
            autoFocus
          />
          <div className="flex justify-end gap-2 mt-3">
            <button
              type="button"
              onClick={onClose}
              className="h-8 px-3 text-xs text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-md"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!note.trim() || saving}
              className="h-8 px-4 text-xs font-semibold text-white rounded-md disabled:opacity-50 bg-primary-500"
            >
              {saving ? 'Saving...' : 'Acknowledge'}
            </button>
          </div>
        </form>

        {!loadingAcks && existingAcks.length > 0 && (
          <div className="px-4 pb-3 border-t border-gray-100">
            <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mt-2.5 mb-2">
              Previous Acknowledgments
            </div>
            <div className="space-y-1.5 max-h-28 overflow-y-auto">
              {existingAcks.map((a) => (
                <div
                  key={a.acknowledgment_id}
                  className="flex items-start gap-2 text-[11px] bg-gray-50 rounded-md px-2.5 py-1.5"
                >
                  <MessageSquare className="w-2.5 h-2.5 text-gray-300 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium text-gray-600">{a.user_name}</span>
                    <span className="text-gray-300 ml-1">
                      {new Date(a.acknowledged_at).toLocaleString('en-ZA')}
                    </span>
                    <p className="text-gray-500 mt-0.5">"{a.note}"</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AcknowledgeModal;
